import React, { useEffect, useState } from 'react';
import { apiFetch } from './api';
import { fetchNews, peekNews } from './newsCache';

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-GB');
};

const NewsPanel = ({ id, bseCode, nseCode }) => {
  const [news, setNews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  // Session-only (not localStorage) - a fresh tab/session starts back at
  // the default (on), per what was asked for.
  const [newsEnabled, setNewsEnabled] = useState(() => sessionStorage.getItem('bse2NewsEnabled') !== 'off');

  useEffect(() => {
    sessionStorage.setItem('bse2NewsEnabled', newsEnabled ? 'on' : 'off');
  }, [newsEnabled]);

  useEffect(() => {
    if (!id || !newsEnabled) return;
    let cancelled = false;
    setQuery('');
    setResults(null);
    // If a prefetch already cached this ticker's news, show it right away.
    const cached = peekNews(id);
    setNews(cached ? cached : []);
    setLoaded(!!cached);
    fetchNews(id, apiFetch)
      .then((rows) => { if (!cancelled) { setNews(rows); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [id, newsEnabled]);

  const search = async (event) => {
    event?.preventDefault();
    const text = query.trim();
    if (text.length < 2) return;
    setSearching(true);
    try {
      const params = new URLSearchParams({ q: text, days: '365' });
      if (bseCode) params.set('bse', bseCode);
      if (nseCode) params.set('nse', nseCode);
      const response = await apiFetch(`/api/news-search?${params}`);
      setResults(response.ok ? await response.json() : []);
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const shownNews = results === null ? news : results;
  return <>
    <div className="news-panel-heading">
      <h4>News</h4>
      <form onSubmit={search}>
        <input aria-label="Search news for this ticker" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search this ticker" />
        <button type="submit" disabled={searching || query.trim().length < 2}>{searching ? '…' : 'Search'}</button>
        {results !== null && <button type="button" onClick={() => { setResults(null); setQuery(''); }}>×</button>}
        <label className="news-toggle" title={newsEnabled ? 'Turn news off' : 'Turn news on'}>
          <input type="checkbox" checked={newsEnabled} onChange={(event) => setNewsEnabled(event.target.checked)} />
          News
        </label>
      </form>
    </div>
    {!newsEnabled ? <div className="news-panel-empty">News is off</div>
      : !loaded && results === null ? <div className="news-panel-loading">Loading news…</div>
      : shownNews.length === 0 ? <div className="news-panel-empty">{results === null ? 'No recent news' : 'No matching news for this ticker'}</div>
        : <ul className="news-panel-list">
          {shownNews.map((item, index) => {
            const headline = item.text || item.HEADLINE || item.NEWSSUB;
            const date = item.date || item.NEWS_DT;
            return <li key={item.NEWSID || `${date}-${index}`}>
              <span className="news-date">{formatDate(date)}</span>
              {item.url ? <a className="news-headline" href={item.url} target="_blank" rel="noreferrer">{headline}</a> : <span className="news-headline">{headline}</span>}
            </li>;
          })}
        </ul>}
  </>;
};

export default NewsPanel;

